import * as React from 'react';
import axios from 'axios';

import api from 'api';
import { AuthStatus, useAuth } from 'auth';
import { TempControlProject } from '__generated-api__';

import { clearProjectStorage, readAuthKeyMap } from 'tempcontrol/api/auth-keys';

/**
 * How many rows the history panel surfaces. Anything beyond this
 * lives in the customer portal index (for owned projects) or stays
 * accessible through deep-linking (for anon drafts on this device).
 */
const HISTORY_DISPLAY_CAP = 10;

export interface TempControlHistoryHook {
  /** Merged drafts from both sources, sorted newest-first, capped to {@link HISTORY_DISPLAY_CAP}. */
  entries: TempControlProject[];
  /** True while the initial hydration is in flight. */
  isLoading: boolean;
  /** Re-read both sources from scratch. */
  refresh: () => Promise<void>;
}

/**
 * Combined draft history for the wizard intro panel.
 *
 * Two independent sources feed the same list:
 *
 *  1. Anonymous "on this device" drafts - read from the
 *     `tempcontrolAuthorizationKeys` localStorage map and hydrated
 *     against the public show endpoint, one round trip per key (in
 *     parallel via `Promise.allSettled`). Stale entries (401/403/404
 *     /410) are evicted on the spot so the panel doesn't keep
 *     dangling rows around. Transient errors (5xx, network) leave
 *     the entry alone.
 *
 *  2. Owned projects (only when `auth.status === LoggedIn`) - read
 *     from the paginated index endpoint with
 *     `ownership=mine&sort=created_at&direction=desc` and capped
 *     server-side. Sales reps and admins explicitly self-scope here
 *     so the wizard panel never accidentally surfaces other users'
 *     work as part of their "your drafts" view; the customer portal
 *     remains the place to browse the full set.
 *
 * The two arrays are deduped by `key` (defensive - owned projects
 * never write to localStorage so a real overlap is impossible after
 * the security fix), sorted by `created_at` descending, and trimmed
 * to {@link HISTORY_DISPLAY_CAP}.
 *
 * The hook deliberately bypasses `useTempControlProject`'s mutation
 * wrappers because those are singleton mutations - firing them
 * concurrently for many keys would race the shared loading state.
 * Direct calls to the generated client keep each request isolated.
 */
export const useTempControlHistory = (): TempControlHistoryHook => {
  const auth = useAuth();
  const isLoggedIn = auth.status === AuthStatus.LoggedIn;
  // While the AuthProvider is still resolving the cookie-bound
  // session (`getCurrentUser()` in flight), `auth.status` sits at
  // `LoggingIn` and we don't yet know whether to call the
  // `?ownership=mine` index. Holding off avoids a flicker where the
  // panel briefly renders anon-only entries before the owned source
  // arrives a moment later.
  const isAuthSettled = auth.status !== AuthStatus.LoggingIn;

  const [entries, setEntries] = React.useState<TempControlProject[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refresh = React.useCallback(async () => {
    if (!isAuthSettled) return;
    if (isMountedRef.current) setIsLoading(true);

    const [anon, owned] = await Promise.all([
      hydrateAnonEntries(),
      isLoggedIn
        ? fetchOwnedEntries().catch(() => [] as TempControlProject[])
        : Promise.resolve([] as TempControlProject[]),
    ]);

    const merged = mergeAndCap(anon, owned, HISTORY_DISPLAY_CAP);

    if (isMountedRef.current) {
      setEntries(merged);
      setIsLoading(false);
    }
  }, [isAuthSettled, isLoggedIn]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  return { entries, isLoading, refresh };
};

/**
 * Hydrate every key in localStorage. Stale entries are evicted as a
 * side effect. Transient errors leave the localStorage row intact so
 * a network blip doesn't wipe a draft the user can still recover.
 */
const hydrateAnonEntries = async (): Promise<TempControlProject[]> => {
  const map = readAuthKeyMap();
  const keys = Object.keys(map);
  if (keys.length === 0) return [];

  const settled = await Promise.allSettled(
    keys.map((key) =>
      api.tempcontrol.getTempControlProject({ key, authorizationKey: map[key] }).then((response) => response.data)
    )
  );

  const hydrated: TempControlProject[] = [];
  settled.forEach((result, index) => {
    const key = keys[index];
    if (result.status === 'fulfilled') {
      hydrated.push(result.value);
      return;
    }
    if (isStaleEntryError(result.reason)) {
      clearProjectStorage(key);
    }
  });

  return hydrated;
};

/**
 * Fetch up to {@link HISTORY_DISPLAY_CAP} most-recent owned projects
 * for the authenticated user. The `ownership=mine` filter is
 * critical for privileged roles (admin / sales rep) - without it the
 * index returns every row in the system, which would drown out the
 * user's own drafts in the wizard panel.
 */
const fetchOwnedEntries = async (): Promise<TempControlProject[]> => {
  const response = await api.tempcontrol.listTempControlProjects({
    ownership: 'mine',
    sort: 'created_at',
    direction: 'desc',
    perPage: HISTORY_DISPLAY_CAP,
    page: 1,
  });
  return response.data.data ?? [];
};

/**
 * Merge anon + owned, dedupe on `key` (anon wins if a duplicate
 * somehow shows up), sort newest-first, and trim to `cap`.
 */
const mergeAndCap = (anon: TempControlProject[], owned: TempControlProject[], cap: number): TempControlProject[] => {
  const seen = new Set<string>();
  const merged: TempControlProject[] = [];
  for (const entry of [...anon, ...owned]) {
    if (seen.has(entry.key)) continue;
    seen.add(entry.key);
    merged.push(entry);
  }
  merged.sort(byCreatedAtDesc);
  return merged.slice(0, cap);
};

/**
 * The backend returns 401 when the Authorization-Key doesn't match,
 * 404 when the project no longer exists, and 410 if it was force-
 * purged. Any of those mean the localStorage entry is dead weight
 * and should be evicted. Anything else (5xx, network failures) is
 * treated as transient and the entry stays.
 */
const isStaleEntryError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === 401 || status === 403 || status === 404 || status === 410;
};

const byCreatedAtDesc = (a: TempControlProject, b: TempControlProject): number => {
  return parseTimestamp(b.created_at) - parseTimestamp(a.created_at);
};

const parseTimestamp = (raw: string | null | undefined): number => {
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : 0;
};
